import React from 'react';
import { Shield, Lock, Eye, Server } from 'lucide-react';

export const Privacy: React.FC = () => {
  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-secondary text-white py-16 px-6 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
            <p className="text-blue-400 font-bold tracking-wider uppercase text-xs mb-2">Legal</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-gray-400 max-w-2xl">
              Last Updated: September 15, 2025
            </p>
        </div>
      </div>

      <div className="bg-white py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          
          {/* Key Highlights */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100">
                <div className="flex items-center gap-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg text-primary"><Shield size={20} /></div>
                    <h3 className="font-bold text-secondary">HIPAA Compliant</h3>
                </div>
                <p className="text-sm text-gray-600">We adhere to strict international standards for health data protection.</p>
            </div>
             <div className="bg-green-50 p-6 rounded-2xl border border-green-100">
                <div className="flex items-center gap-3 mb-3">
                    <div className="bg-green-100 p-2 rounded-lg text-green-600"><Lock size={20} /></div>
                    <h3 className="font-bold text-secondary">End-to-End Encryption</h3>
                </div>
                <p className="text-sm text-gray-600">Your video consultations and chat logs are encrypted and private.</p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-12 text-gray-600 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">1. Introduction</h2>
              <p>
                Men's Health Clinic Africa ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">2. Information We Collect</h2>
              <p className="mb-4">We collect information that identifies, relates to, describes, references, is capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular consumer or device.</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Personal Identification:</strong> Name, email address, phone number, date of birth.</li>
                <li><strong>Health Data:</strong> Medical history, symptoms, prescriptions, lab results, and vitals.</li>
                <li><strong>Payment Information:</strong> Credit card details and billing address (processed via secure third-party gateways).</li>
                <li><strong>Device Data:</strong> IP address, device type, operating system version.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and manage your telemedicine appointments.</li>
                <li>To process prescriptions and lab test orders.</li>
                <li>To send you appointment reminders and health updates.</li>
                <li>To improve our application functionality and user experience.</li>
                <li>To comply with legal and regulatory obligations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">4. Data Sharing and Disclosure</h2>
              <p>
                We do not sell your personal data. We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Healthcare Providers:</strong> Doctors and specialists you book with to facilitate your care.</li>
                <li><strong>Pharmacies & Labs:</strong> To fulfill prescription orders and process test results.</li>
                <li><strong>Service Providers:</strong> Cloud hosting, payment processors, and analytics providers who are bound by confidentiality agreements.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">5. Your Rights</h2>
              <p>
                Depending on your jurisdiction, you may have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Request access to the personal data we hold about you.</li>
                <li>Request correction of inaccurate or incomplete data.</li>
                <li>Request deletion of your account and associated data.</li>
                <li>Withdraw consent for marketing communications.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">6. Contact Us</h2>
              <p>
                If you have questions or comments about this policy, please contact our Data Protection Officer at:
              </p>
              <div className="mt-4 bg-gray-50 p-6 rounded-xl inline-block">
                <p className="font-bold text-secondary">Men's Health Clinic Africa</p>
                <p>281a Herbert Chitepo Avenue</p>
                <p>Harare</p>
                <p className="mt-2 text-primary font-medium">support@menshealthclinic.africa</p>
              </div>
            </section>
          </div>

        </div>
      </div>
    </div>
  );
};